import { apmBase } from '@elastic/apm-rum';
import type { App } from 'vue';
import type { Router } from 'vue-router';

let networkDown = false;
let networkRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
// APM flushes every 15 s; wait 16 s after coming back online to avoid sending
const NETWORK_RECOVERY_DELAY_MS = 16_000;

function trackNetworkState(): void {
    globalThis.addEventListener('offline', () => {
        if (networkRecoveryTimer) {
            clearTimeout(networkRecoveryTimer);
            networkRecoveryTimer = null;
        }
        networkDown = true;
    });
    globalThis.addEventListener('online', () => {
        networkRecoveryTimer = setTimeout(() => {
            networkDown = false;
            networkRecoveryTimer = null;
        }, NETWORK_RECOVERY_DELAY_MS);
    });
}

function isNetworkDown(): boolean {
    return !navigator.onLine || networkDown;
}

const IGNORED_ERROR_PATTERNS = [
    // Network / user-cancelled requests
    /AbortError/,
    /Failed to fetch/,
    // Safari equivalents of "Failed to fetch"
    /TypeError: Load failed/,
    // Chrome/Firefox error thrown when a dynamic import() chunk fails to fetch from CDN
    /error loading dynamically imported module/,
    // Safari equivalent
    /Importing a module script failed/,
    /Unable to preload CSS/,
    // Safari fires this when a speculatively preloaded script is blocked by CORS during preload;
    // the browser refetches it normally afterwards, so it is preload noise, not an app bug.
    /Cross-origin script load denied/,
    /WebSocket closed with status code: 100[16]/,
    /timeout of \d+ms exceeded/,
    /status code 429/,
    /status code 401/,
    /Request aborted/,

    // Browser API noise
    /ResizeObserver loop/,
    /^Script error\.?$/,
    /SecurityError: The operation is insecure/,

    // Chromium/V8 engine bug thrown by the browser itself (not app logic) when JS accesses
    // an object on a torn-down execution context, e.g. during Apollo/zen-observable teardown.
    // Chromium-only literal (Safari/Firefox emit different strings); observed only on old
    // Android WebView / forked Chromium (Chrome 87/92). Not reproducible on updated Chrome.
    /^Uncaught illegal access$/,

    // isTrusted DOM event rejected as promise (JSON.stringify uses double quotes)
    /"isTrusted"\s*:\s*true/,

    // Known app behaviors, not actionable bugs
    /getAuthToken died/,
    /ApolloError: Load failed/,
    /NetworkError: Load failed/,
    // Chrome/Android equivalent of Safari's "Load failed" — network drop on the device
    /ApolloError: NetworkError when attempting to fetch resource/,
    /Server timeout elapsed without receiving a message from the server/,
    /\[Authorization\] Expired/,

    // SignalR hubs (Jackpot, BetBuilder) disconnect when Android Chrome are frozen in background.
    // The server closes the connection after the ping timeout, producing an unhandled rejection
    // inside SignalR's _processIncomingData. withAutomaticReconnect() handles recovery silently.
    /Client hasn't sent a message\/ping within the configured ClientTimeoutInterval/,

    // SignalR handshake canceled by the server during mobile network instability
    // or carrier NAT interruption. Sporadic (< 2/day), no server-side impact; Rogue REST polling
    // provides fallback coverage for maxOdds updates while the hub is unreachable.
    /Server returned handshake error: Handshake was canceled/,
    /The connection was stopped before the hub handshake could complete/,

    // SignalR race condition: the WebSocket closes (e.g. Android WebView backgrounded by host app)
    // between the connection-state check in safeInvoke and the actual invoke call. The onclose
    // handler triggers automatic reconnect and re-subscription; no data is lost.
    /Invocation canceled due to the underlying connection being closed/,
    /WebSocket is not in the OPEN state/,

    // Third-party WebView injected script errors (e.g. Zalo iOS bridge)
    /zaloJSV2/,

    // Vue scheduler race condition during iOS Safari back navigation: the swipe-back gesture
    // interrupts the scheduler flush mid-mount, causing insertBefore to receive a null parent.
    // Unfixable at app level; known Vue edge case (vuejs/router#1765, vuejs/core#5657).
    /Cannot read properties of null \(reading 'insertBefore'\)/,

    // Same teardown-vs-scheduled-update race as insertBefore above, hit via a different renderer
    // accessor: a component update job (ErrorCodes.COMPONENT_UPDATE) runs hostParentNode(anchor)
    // after RouterView has already detached the anchor during a route change. Unfixable at app
    // level; known Vue edge case (vuejs/core#5657, vuejs/router#1765).
    /Cannot read properties of null \(reading 'parentNode'\)/,
];

// Safari/WebKit error from apm-rum-core's own fetch-patch; culprit varies, so check stacktrace origin too.
const APM_RUM_CORE_SYNTAX_ERROR_MESSAGE = /The string did not match the expected pattern/;
const APM_RUM_CORE_FRAME_PATTERN = /@elastic[/+]apm-rum-core/;

function isThrownFromApmRumCore(err: any): boolean {
    const frames = [...(err.exception?.stacktrace ?? []), ...(err.log?.stacktrace ?? [])];
    return frames.some(frame => APM_RUM_CORE_FRAME_PATTERN.test(frame?.filename ?? '') || APM_RUM_CORE_FRAME_PATTERN.test(frame?.abs_path ?? ''));
}

// axios's own wording; the interceptor already reported it (or skipped it on purpose), so any later copy is a duplicate.
const AXIOS_HTTP_FAILURE_MESSAGE = /Request failed with status code \d{3}/;

const JACKPOT_READONLY_ERROR_MESSAGE = /Attempted to assign to readonly property/;
const JACKPOT_ODOMETER_CULPRIT_PATTERN = /useJackpotData|Odometer/;

function isJackpotOdometerReadonlyError(err: any): boolean {
    const message = err.exception?.message ?? err.log?.message ?? '';
    if (!JACKPOT_READONLY_ERROR_MESSAGE.test(message)) return false;

    return JACKPOT_ODOMETER_CULPRIT_PATTERN.test(err.culprit ?? '');
}

const IGNORED_CULPRIT_PATTERNS = [
    // Vite's modulepreload polyfill only calls fetch() to preload chunks — no app logic.
    // Any error originating here is browser/network noise and is never actionable.
    /modulepreload-polyfill/,
];

const ALLOWED_CULPRIT_PATTERNS = [
    /\/src\//, // source-map resolved paths (e.g. ../../src/components/...)
    /node_modules\//, // any node_modules path (Vite deps, pnpm, etc.)
    /^assets\//, // build output served from the project's own origin
];

export function isProjectCdnCulprit(culprit: string, cdnOrigin = __SPORTS_CDN_URL__): boolean {
    return !!(cdnOrigin && culprit.startsWith(cdnOrigin));
}

function getIosSystemVersion(): number | null {
    const match = /CPU (?:iPhone |iPad )?OS (\d+)_/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function getSafariVersion(): number | null {
    const ua = navigator.userAgent;
    if (/Chrome\//.exec(ua) !== null || /Edg\//.exec(ua) !== null) return null;
    const match = /Version\/(\d+)/.exec(ua);
    return match ? Number(match[1]) : null;
}

function getEdgeVersion(): number | null {
    const match = /Edg\/(\d+)/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function getChromeVersion(): number | null {
    const ua = navigator.userAgent;
    if (/Edg\//.exec(ua) !== null || /SamsungBrowser\//.exec(ua) !== null) return null;
    const match = /Chrome\/(\d+)/.exec(ua);
    return match ? Number(match[1]) : null;
}

function getFirefoxVersion(): number | null {
    const match = /Firefox\/(\d+)/.exec(navigator.userAgent);
    return match ? Number(match[1]) : null;
}

function isUnsupportedBrowser(): boolean {
    // All browsers on iOS use WebKit; capabilities are controlled by iOS version
    const iosVersion = getIosSystemVersion();
    if (iosVersion !== null) return iosVersion < 14;

    const safariVersion = getSafariVersion();
    if (safariVersion !== null) return safariVersion < 14;

    const edgeVersion = getEdgeVersion();
    if (edgeVersion !== null) return edgeVersion < 126;

    const chromeVersion = getChromeVersion();
    if (chromeVersion !== null) return chromeVersion < 91;

    const firefoxVersion = getFirefoxVersion();
    if (firefoxVersion !== null) return firefoxVersion < 125;

    return false;
}

function shouldKeepError(err: any) {
    const message = err.exception?.message ?? err.log?.message ?? '';
    const type = err.exception?.type ?? '';
    const fullMessage = type ? `${type}: ${message}` : message;
    const culprit = err.culprit ?? '';

    if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.exec(message) !== null || pattern.exec(fullMessage) !== null)) return false;
    if (AXIOS_HTTP_FAILURE_MESSAGE.test(fullMessage)) return false;
    if (APM_RUM_CORE_SYNTAX_ERROR_MESSAGE.test(fullMessage) && isThrownFromApmRumCore(err)) return false;
    if (isJackpotOdometerReadonlyError(err)) return false;
    if (IGNORED_CULPRIT_PATTERNS.some(pattern => pattern.exec(culprit) !== null)) return false;
    if (culprit && !ALLOWED_CULPRIT_PATTERNS.some(p => p.exec(culprit) !== null) && !isProjectCdnCulprit(culprit)) return false;

    return true;
}

// User-Agents of automated clients that are never real players. Anchor each pattern so it cannot catch a real browser.
const BOT_USER_AGENT_PATTERNS = [
    // Bot scanning mirror domains (Castor APM, 31/10–01/11/2026: 60 errors from Google Cloud IPs, no logged-in user).
    /^noua$/,
];

function isBotUserAgent(): boolean {
    return BOT_USER_AGENT_PATTERNS.some(pattern => pattern.test(navigator.userAgent));
}

function filterApmPayload(payload: any) {
    if (isNetworkDown()) return null;
    if (isBotUserAgent()) return null;
    if (!payload.errors) return payload;
    if (isUnsupportedBrowser()) return null;

    payload.errors = payload.errors.filter(shouldKeepError);

    return payload.errors.length > 0 ? payload : null;
}

export function registerApmFilters() {
    trackNetworkState();
    apmBase.addFilter(filterApmPayload);
}

// Takes over the Vue error handler (install the plugin with captureErrors: false) to append the
// component name, lifecycle hook and route to the message — the field APM indexes and shouldKeepError
// matches, so IGNORED_ERROR_PATTERNS keep working. Needed because the plugin reads vm.$options.name,
// which is empty for <script setup> in production.
function getVueComponentName(instance: any): string {
    if (!instance) return 'unknown';
    if (instance.$root === instance) return 'Root';
    return instance.$?.type?.__name ?? instance.$options?.name ?? instance.$options?.__name ?? instance.$options?._componentTag ?? 'Anonymous';
}

function describeEventTarget(target: EventTarget | null): string | undefined {
    if (target instanceof HTMLScriptElement || target instanceof HTMLImageElement) return target.src;
    if (target instanceof HTMLElement) return target.tagName.toLowerCase();
    return '';
}

// Never String(value) here: a rejected DOM event (e.g. a blocked <script> load) would collapse to
// "[object Event]", which tells us nothing and cannot be matched by the isTrusted filter above.
// Events are stringified field by field because Firefox exposes no own enumerable properties on
// them, so JSON.stringify alone would flatten them to "{}".
function ensureError(err: unknown): Error {
    if (err instanceof Error) return err;
    if (typeof err === 'string') return new Error(err);
    if (err instanceof Event) {
        return new Error(JSON.stringify({
            isTrusted: err.isTrusted,
            type: err.type,
            target: describeEventTarget(err.target),
        }));
    }

    return new Error(JSON.stringify(err));
}

export function registerVueErrorContext(app: App, router: Router): void {
    app.config.errorHandler = (err, instance, info) => {
        const error = ensureError(err);
        const component = getVueComponentName(instance);
        const routeName = String(router.currentRoute.value.name ?? 'unknown');
        error.message = `${error.message} [component=${component}] [hook=${info || 'unknown'}] [route=${routeName}]`;
        apmBase.captureError(error);
        console.error(error);
    };
}
